import * as React from "react";
import {
  NavLink,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  CreditCard,
  Clock,
  FileText,
  ScrollText,
  DollarSign,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { CustomerAccountSection } from "./components/CustomerAccountSection";
import { AgingTab } from "./components/AgingTab";
import { PaymentPlansTab } from "./components/PaymentPlansTab";
import { AuditLogTab } from "./components/AuditLogTab";
import { CashFlowTab } from "./components/CashFlowTab";

const sections = [
  { value: "account", label: "Customer Account", icon: CreditCard },
  { value: "cashflow", label: "Cash Flow (AR)", icon: DollarSign },
  { value: "aging", label: "Overdue / Aging", icon: Clock },
  { value: "plans", label: "Payment Plans", icon: FileText },
  { value: "audit", label: "Audit Log", icon: ScrollText },
] as const;

type AccountingSection = (typeof sections)[number]["value"];
const validSections = new Set<AccountingSection>(
  sections.map((section) => section.value),
);
const sectionAliases: Record<string, AccountingSection> = {
  overdue: "aging",
  payments: "account",
  financial: "account",
  history: "account",
  points: "account",
};

const sectionComponents: Record<AccountingSection, React.ComponentType> = {
  account: CustomerAccountSection,
  cashflow: CashFlowTab,
  aging: AgingTab,
  plans: PaymentPlansTab,
  audit: AuditLogTab,
};

function normalizeSection(
  rawSection: string | null | undefined,
): AccountingSection | null {
  if (!rawSection) return null;

  const aliasedSection = sectionAliases[rawSection] ?? rawSection;
  return validSections.has(aliasedSection as AccountingSection)
    ? (aliasedSection as AccountingSection)
    : null;
}

function toSectionPath(section: AccountingSection): string {
  return section === "account"
    ? "/admin/accounting"
    : `/admin/accounting/${section}`;
}

export default function AccountingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { section } = useParams<{ section?: string }>();

  const queryParams = React.useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const routeSection = normalizeSection(section);
  const queryTabSection = normalizeSection(queryParams.get("tab"));
  const activeSection: AccountingSection = routeSection ?? queryTabSection ?? "account";

  const canonicalPath = toSectionPath(activeSection);
  const canonicalSearchParams = React.useMemo(() => {
    const nextParams = new URLSearchParams(location.search);
    nextParams.delete("tab");
    return nextParams.toString();
  }, [location.search]);
  const canonicalUrl = canonicalSearchParams
    ? `${canonicalPath}?${canonicalSearchParams}`
    : canonicalPath;

  React.useEffect(() => {
    const currentUrl = `${location.pathname}${location.search}`;
    if (currentUrl !== canonicalUrl) {
      navigate(canonicalUrl, { replace: true });
    }
  }, [canonicalUrl, location.pathname, location.search, navigate]);

  const ActiveSectionComponent = sectionComponents[activeSection];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Accounting</h1>
        <p className="text-muted-foreground mt-1">
          Customer accounts, cash flow, aging, payment plans, and audit trail
        </p>
      </div>

      <nav className="flex w-full flex-wrap items-center gap-1 rounded-lg bg-muted p-[3px] text-muted-foreground">
        {sections.map((sectionLink) => {
          const Icon = sectionLink.icon;
          const sectionPath = toSectionPath(sectionLink.value);
          const isRootSection = sectionLink.value === "account";

          return (
            <NavLink
              key={sectionLink.value}
              to={sectionPath}
              end={isRootSection}
              className={({ isActive }) =>
                cn(
                  "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:outline-ring inline-flex items-center justify-center gap-2 rounded-md border px-3 py-1 text-sm font-medium whitespace-nowrap transition-all focus-visible:ring-[3px] focus-visible:outline-1",
                  isActive
                    ? "bg-background text-foreground border-transparent shadow-sm"
                    : "text-foreground/60 border-transparent hover:text-foreground",
                )
              }
            >
              <Icon className="h-4 w-4" />
              {sectionLink.label}
            </NavLink>
          );
        })}
      </nav>

      <ActiveSectionComponent />
    </div>
  );
}
